import styles from './deliveryNotePDFPage.module.css'
import { useEffect, useMemo, useState } from 'react'
import { Labels, useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { PDFHeader } from '../../../../../features/ui'
import { Address, addressHelpers } from '../../../../../features/address'
import { Customer, SaleOperation, SalePaymentItem, useCustomerLoaders, useSaleOperationLoaders, useSalesHooks, useSaleOperationHooks } from '../../../../sales'
import { firstItem } from '../../../../../utils/listUtils'
import { useDeliveryOrderHooks } from '../../../hooks/deliveryOrderHooks'
import { DocumentKey, Employee, PaymentType, useEmployeeLoaders, usePaymentTypeLoaders } from '../../../../administration'
import { DeliveryNoteItemPDFRow } from './types'
import { useProductHooks, useProductLoaders, Product } from '../../../../products'
import { deliveryHelpers, useDeliveryHooks } from '../../../hooks/deliveryHooks'
import { Delivery, DeliveryMethod, DeliveryOrder } from '../../../state/types'

type Props = {
    pageIndex: number
    delivery: Delivery
    deliveryOrder?: DeliveryOrder
    pagesAmount: number
}

export const DeliveryNotePDFPage = (props: Props) => {
    const { pageIndex, delivery, deliveryOrder, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1

    const [saleOperation, setSaleOperation] = useState<SaleOperation>()
    const [paymentType, setPaymentType] = useState<PaymentType>()
    const [paymentItems, setPaymentItems] = useState<SalePaymentItem[]>([])
    const [customer, setCustomer] = useState<Customer>()
    const [seller, setSeller] = useState<Employee>()
    const [address, setAddress] = useState<Address>()
    const [products, setProducts] = useState<Product[]>([])
    const [weightDescription, setWeightDescription] = useState<string>()
    const [rows, setRows] = useState<DeliveryNoteItemPDFRow[]>([])

    const { describeAddress } = addressHelpers
    const { describeDeliveryNote } = deliveryHelpers
    const { formatNumber, formatDate, formatDateTime, formatIdentifier, formatPhone } = useDataTypesHooks()
    const { describe } = useDescribeHooks()
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const { describeWeight } = useDeliveryHooks()
    const { describeMeasure, describeQuantity } = useProductHooks()
    const { getDocumentFooter } = useSalesHooks()
    const { describePaymentItemName } = useSaleOperationHooks()

    const saleOperationLoaders = useSaleOperationLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const customerLoaders = useCustomerLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const productLoaders = useProductLoaders()

    const load = async () => {
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(delivery.customerId!)
        setCustomer(loadedCustomer)
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedSaleOperation = await loadSaleOperation(delivery.table, delivery.operationId)
        setSaleOperation(loadedSaleOperation)
        const { findPaymentType } = paymentTypeLoaders()
        const paymentType = findPaymentType(loadedSaleOperation!.paymentTypeId)
        setPaymentType(paymentType)
        setPaymentItems(loadedSaleOperation!.paymentItems)
        const { loadEmployee } = employeeLoaders()
        const loadedEmployee = await loadEmployee(loadedSaleOperation!.sellerId)
        setSeller(loadedEmployee)
        const deliveryAddress = await getDeliveryAddress(delivery)
        setAddress(deliveryAddress)
        const productIds = loadedSaleOperation!.items.map(item => item.productId)
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(productIds)
        setProducts(loadedProducts)
        const loadedWeightDescription = await describeWeight(delivery)
        setWeightDescription(loadedWeightDescription)
    }

    useEffect(() => {
        load()
    }, [delivery, saleOperation])

    const loadRows = async () => {
        if (saleOperation && products.length > 0) {
            return delivery.items
                .filter(deliveryItem => deliveryItem.quantity > 0)
                .map(deliveryItem => {
                    const item = saleOperation.items.find(item => item.id === deliveryItem.itemId)!
                    const product = products.find(product => product.id === item.productId)!
                    return {
                        product: product.name,
                        measure: describeMeasure(product),
                        quantity: describeQuantity(deliveryItem.quantity, product),
                        deliveredQuantity: formatNumber(deliveryItem.deliveredQuantity),
                        notDeliveredQuantity: formatNumber(deliveryItem.notDeliveredQuantity),
                        defectiveQuantity: formatNumber(deliveryItem.defectiveQuantity || 0),
                        nonDefectiveQuantity: formatNumber(deliveryItem.quantity - (deliveryItem.defectiveQuantity || 0))
                    }
                })
        }

        return []
    }

    useEffect(() => {
        loadRows().then(setRows)
    }, [saleOperation?.items, products])

    const columns = useMemo(() => {
        // A return note shows the returned quantity split into defective and non defective items
        if (delivery.method === DeliveryMethod.RETURN) {
            return [
                { name: 'product', label: 'Producto' },
                { name: 'measure', label: 'Medida' },
                { name: 'quantity', label: 'Devueltos' },
                { name: 'defectiveQuantity', label: 'Defectuosos' },
                { name: 'nonDefectiveQuantity', label: 'Sin Defecto' }
            ]
        }
        const columnList = [
            { name: 'product', label: 'Producto' },
            { name: 'measure', label: 'Medida' },
            { name: 'quantity', label: 'Cantidad' }
        ]
        if (delivery.items.some(item => item.deliveredQuantity > 0)) {
            columnList.push({ name: 'deliveredQuantity', label: 'Entregados' })
        }
        if (delivery.items.some(item => item.notDeliveredQuantity > 0)) {
            columnList.push({ name: 'notDeliveredQuantity', label: 'No Entregados' })
        }
        return columnList
    }, [delivery.items, delivery.method])

    const notDeliveredItemsText = useMemo(() => {
        if (!saleOperation || products.length === 0 || !delivery.fromNotDeliveredItems || delivery.fromNotDeliveredItems.length === 0) return null
        const itemsText = delivery.fromNotDeliveredItems.map(notDeliveredItem => {
            const item = saleOperation.items.find(item => item.id === notDeliveredItem.itemId)!
            const product = products.find(product => product.id === item.productId)!
            return `${product.name} (${notDeliveredItem.quantity})`
        })
        return `* Algunos items corresponden a items no entregados de remitos anteriores: ${itemsText.join(', ')}.`
    }, [saleOperation, products, delivery.fromNotDeliveredItems])

    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.DELIVERY_NOTE), [getDocumentFooter])

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <PDFHeader taxInfo />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <div className={styles.titleSection}>
                            <span className={styles.title}>
                                {describeDeliveryNote(delivery, deliveryOrder)}
                            </span>
                            {saleOperation && (
                                <span className={styles.operation}>
                                    {describe(delivery.table, saleOperation)}
                                </span>
                            )}
                        </div>
                        <div className={styles.date}>
                            Fecha: {formatDateTime(new Date(delivery.creationDate))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Cliente: {customer?.name}</span>
                            <span>Dirección: {customer?.mainAddress ? describeAddress(customer?.mainAddress) : '-'}</span>
                            <span>DNI/CUIT: {customer?.identifier ? formatIdentifier(customer.identifier) : '-'}</span>
                            <span>Teléfono: {formatPhone(firstItem(customer?.phones || []))}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Vendedor: {seller?.name}</span>
                            <span>Teléfono: {formatPhone(seller?.phone) || '-'}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={`${styles.infoSection} ${styles.wide}`}>
                            <span>Cond. IVA: {customer?.vatCondition ? Labels.vatConditionLabel(customer.vatCondition) : '-'}</span>
                            {paymentItems.length > 0 ? (
                                <span>
                                    Medios de Pago:
                                    {paymentItems.map((paymentItem, index) =>
                                        `${index !== 0 ? ',' : ''} ${describePaymentItemName(paymentItem)}`
                                    )}
                                </span>
                            ) : (
                                <span>Medio de Pago: {paymentType?.name}</span>
                            )}
                        </div>
                    </div>
                </>)}

                <div className={styles.tableRow}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <th key={index}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        {columns.map((column, index) => (
                                            <td key={index}>
                                                {row[column.name as keyof DeliveryNoteItemPDFRow]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles.pagination}>
                    {pageIndex + 1} / {pagesAmount}
                </div>

                {isLastPage && (
                    <div className={`${styles.row} ${styles.deliveryInfo}`}>
                        <span>Peso Aproximado: {weightDescription}</span>
                        <span>Fecha de Entrega: {formatDate(new Date(delivery.date))}</span>
                        <span>Entregar en: {address ? describeAddress(address) : '-'}</span>
                    </div>
                )}

                <div className={styles.footer}>
                    {isLastPage && saleOperation?.notes && (
                        <div className={styles.row}>
                            <span className={styles.notes}>Notas: {saleOperation.notes}</span>
                        </div>
                    )}
                    {notDeliveredItemsText && (
                        <div className={styles.row}>
                            <span className={styles.notes}>{notDeliveredItemsText}</span>
                        </div>
                    )}
                    <div className={styles.separator} />
                    <div className={`${styles.row} ${styles.footerRow}`}>
                        <span>
                            {documentFooter}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}
